/* Lifecycle-safe components: work on first load and Shopify section re-render. */
(() => {
  if (window.blackFive2026) return;
  window.blackFive2026 = true;
  let pendingCartScroll = null;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Translation apps can replace both the option label and its value (for example
  // Black -> Czarny). CSS cannot use translated words as colors, so normalize
  // known color words back to a visual color while preserving the translated label.
  const translatedColor = value => {
    const key = String(value || '').toLowerCase();
    const colors = [
      [/black|czarn|schwarz|noir|negro|nero|zwart|svart|sort|preto|μαύρ|czerń/, '#171717'],
      [/white|biały|weiss|blanc|blanco|bianco|wit|vit|hvid|branco|λευκ/, '#f4f4f4'],
      [/grey|gray|szar|grau|gris|grigio|grijs|grå|cinz|γκρι/, '#8b8e8f'],
      [/blue|błękit|niebies|blau|bleu|azul|blu|blauw|blå|μπλε/, '#315887'],
      [/green|zieleń|zielon|grün|vert|verde|groen|grön|grøn|πράσι/, '#35725a'],
      [/purple|fiolet|lila|violet|mor|viola|paars|lila|μωβ/, '#c5b3da'],
      [/orange|pomara|naranja|aranc|oranje|orange|πορτοκαλ/, '#ff9d00'],
      [/beige|champagne|szampan|gold|złot|oro|or|goud|зол/, '#dfcca0'],
      [/brown|brąz|brun|braun|marron|marrón|marrone|bruin|brun|καφέ/, '#8a6545']
    ];
    return colors.find(([pattern]) => pattern.test(key))?.[1] || '';
  };
  const normalizeSwatches = (root = document) => root.querySelectorAll('.b5-swatch').forEach(label => {
    const value = label.getAttribute('title') || label.querySelector('input')?.value;
    const color = translatedColor(value);
    const swatch = label.querySelector('span:not(.visually-hidden)');
    if (color && swatch && swatch.style.getPropertyValue('--swatch') !== color) swatch.style.setProperty('--swatch', color);
  });
  const campaignComparePrice = current => {
    const digits = String(current || '').replace(/[^0-9]/g, '');
    return ({ '1399': '€1499', '1899': '€2099', '1199': '€1299', '999': '€1099' })[digits] || '';
  };
  const ensureCampaignComparePrice = (root = document) => root.querySelectorAll('[data-b5-price]').forEach(price => {
    const compare = price.querySelector('[data-b5-compare]');
    const current = price.querySelector('[data-b5-current]');
    if (!compare || !current || compare.textContent.trim()) return;
    const fallback = campaignComparePrice(current.textContent);
    if (fallback) {
      compare.textContent = fallback;
      compare.dataset.b5Fallback = fallback;
      compare.hidden = false;
    }
  });
  normalizeSwatches();
  ensureCampaignComparePrice();
  new MutationObserver(() => normalizeSwatches()).observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['title', 'value', 'style'] });
  class BlackFiveGallery extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.track = this.querySelector('[data-b5-track]');
      if (!this.track) return;
      this.slides = [...this.track.children].filter(el => el.hasAttribute('data-b5-slide'));
      this.dots = [...this.querySelectorAll('[data-b5-dot]')];
      this.progress = this.querySelector('[data-b5-progress]');
      const on = (el, event, fn) => el?.addEventListener(event, fn, { signal });
      on(this.querySelector('[data-b5-prev]'), 'click', () => this.step(-1));
      on(this.querySelector('[data-b5-next]'), 'click', () => this.step(1));
      this.dots.forEach(dot => on(dot, 'click', () => this.go(Number(dot.dataset.b5Dot))));
      if (this.progress?.matches('input')) on(this.progress, 'input', () => { this.track.scrollLeft = Number(this.progress.value) / 100 * this.maxScroll(); });
      if (this.progress && !this.progress.matches('input')) {
        const setProgress = event => {
          const rect = this.progress.getBoundingClientRect();
          const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
          this.track.scrollLeft = ratio * this.maxScroll();
          this.update();
        };
        on(this.progress, 'pointerdown', event => {
          if (this.maxScroll() <= 2) return;
          event.preventDefault();
          this.progress.setPointerCapture?.(event.pointerId);
          this.progressDrag = event.pointerId;
          setProgress(event);
        });
        on(this.progress, 'pointermove', event => {
          if (event.pointerId === this.progressDrag) setProgress(event);
        });
        on(this.progress, 'click', setProgress);
        const endProgressDrag = event => {
          if (event.pointerId === this.progressDrag) {
            this.progress.releasePointerCapture?.(event.pointerId);
            this.progressDrag = null;
          }
        };
        on(this.progress, 'pointerup', endProgressDrag);
        on(this.progress, 'pointercancel', endProgressDrag);
      }
      on(this.track, 'scroll', () => this.update());
      on(this.track, 'keydown', event => {
        if (event.target !== this.track) return;
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          if (event.key === 'Home') this.go(0);
          else if (event.key === 'End') this.go(this.slides.length - 1);
          else this.step(event.key === 'ArrowRight' ? 1 : -1);
        }
      });
      // Touch uses native scrolling. Pointer dragging adds desktop support without blocking links.
      on(this.track, 'pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button,input,select,label')) return;
        this.drag = { x: event.clientX, scroll: this.track.scrollLeft, id: event.pointerId, moved: false };
      });
      on(window, 'pointermove', event => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const distance = event.clientX - this.drag.x;
        if (Math.abs(distance) > 5) {
          this.drag.moved = true;
          this.track.classList.add('is-dragging');
          this.track.setPointerCapture(event.pointerId);
          this.track.scrollLeft = this.drag.scroll - distance;
        }
      });
      const endDrag = () => {
        if (!this.drag) return;
        this.suppressClick = this.drag.moved;
        if (this.track.hasPointerCapture(this.drag.id)) this.track.releasePointerCapture(this.drag.id);
        this.track.classList.remove('is-dragging');
        this.drag = null;
        clearTimeout(this.clickTimer);
        this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 0);
      };
      on(window, 'pointerup', endDrag);
      on(window, 'pointercancel', endDrag);
      on(this.track, 'dragstart', event => event.preventDefault());
      this.track.addEventListener('click', event => { if (this.suppressClick) { event.preventDefault(); event.stopPropagation(); } }, { signal, capture: true });
      on(this, 'mouseenter', () => { this.hovered = true; this.stop(); });
      on(this, 'mouseleave', () => { this.hovered = false; this.play(); });
      on(this, 'focusin', () => this.stop());
      on(this, 'focusout', () => { clearTimeout(this.focusTimer); this.focusTimer = setTimeout(() => this.play(), 0); });
      on(document, 'visibilitychange', () => document.hidden ? this.stop() : this.play());
      on(this.querySelector('[data-b5-pause]'), 'click', event => {
        this.paused = !this.paused;
        event.currentTarget.setAttribute('aria-pressed', String(this.paused));
        event.currentTarget.textContent = this.paused ? event.currentTarget.dataset.playLabel : event.currentTarget.dataset.pauseLabel;
        this.paused ? this.stop() : this.play();
      });
      this.resize = new ResizeObserver(() => this.update());
      this.resize.observe(this.track);
      this.update();
      if (!this.classList.contains('b5-gallery--product')) this.play();
    }
    maxScroll() { return Math.max(0, this.track.scrollWidth - this.track.clientWidth); }
    position(index) { return this.slides[index] ? this.slides[index].offsetLeft - this.slides[0].offsetLeft : 0; }
    go(index, instant = false) { this.track.scrollTo({ left: this.position(index), behavior: instant || reducedMotion() ? 'auto' : 'smooth' }); }
    step(direction) {
      if (this.hasAttribute('data-carousel')) {
        const step = this.slides.length > 1 ? this.position(1) : this.track.clientWidth;
        this.track.scrollBy({ left: direction * step, behavior: reducedMotion() ? 'auto' : 'smooth' });
      } else this.go(Math.max(0, Math.min(this.slides.length - 1, this.index + direction)));
    }
    update() {
      if (!this.slides.length) return;
      this.index = this.slides.reduce((best, slide, i) => Math.abs(this.position(i) - this.track.scrollLeft) < Math.abs(this.position(best) - this.track.scrollLeft) ? i : best, 0);
      const max = this.maxScroll();
      const prev = this.querySelector('[data-b5-prev]');
      const next = this.querySelector('[data-b5-next]');
      if (prev) { prev.disabled = this.track.scrollLeft <= 2; prev.hidden = max <= 2; }
      if (next) { next.disabled = this.track.scrollLeft >= max - 2; next.hidden = max <= 2; }
      if (this.progress) {
        const scrollRatio = max > 0 ? Math.max(0, Math.min(1, this.track.scrollLeft / max)) : 1;
        const firstSlide = this.slides[0];
        const secondSlide = this.slides[1];
        const slideStep = secondSlide ? Math.max(1, this.position(1)) : Math.max(1, firstSlide.offsetWidth);
        const visibleCount = Math.max(1, Math.min(this.slides.length, this.track.clientWidth / slideStep));
        const continuousStep = visibleCount + (this.slides.length - visibleCount) * scrollRatio;
        const percent = continuousStep / this.slides.length * 100;
        if (this.progress.matches('input')) {
          this.progress.value = percent;
          this.progress.disabled = max <= 2;
        } else {
          this.progress.style.setProperty('--b5-current', String(continuousStep));
          this.progress.style.setProperty('--b5-remaining', String(Math.max(0, this.slides.length - continuousStep)));
          this.progress.style.setProperty('--b5-progress-percent', String(percent));
          this.progress.setAttribute('aria-valuenow', continuousStep.toFixed(2));
          this.progress.classList.toggle('is-complete', scrollRatio >= .999);
        }
      }
      this.dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === this.index)));
      if (this.classList.contains('b5-gallery--product') && this.dataset.b5CustomGallery === 'true') {
        const product = this.closest('b5-product');
        if (product?.syncGalleryToIndex) product.syncGalleryToIndex(this.index);
      }
    }
    stop() { clearInterval(this.timer); }
    play() {
      this.stop();
      if (!this.dataset.autoplay || this.slides.length < 2 || this.paused || this.hovered || document.hidden || this.contains(document.activeElement) || reducedMotion() || window.Shopify?.designMode) return;
      this.timer = setInterval(() => this.go((this.index + 1) % this.slides.length), Number(this.dataset.autoplay));
    }
    disconnectedCallback() { this.abort?.abort(); this.abort = null; this.resize?.disconnect(); this.stop(); clearTimeout(this.clickTimer); clearTimeout(this.focusTimer); }
  }
  class BlackFiveProduct extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      try { this.variants = JSON.parse(this.querySelector('[data-b5-variants]')?.textContent || '[]'); } catch { this.variants = []; }
      this.addEventListener('change', event => {
        if (!event.target.closest('[data-b5-option]')) return;
        const slide = event.target.dataset.b5GallerySlide;
        const gallery = this.querySelector('b5-gallery');
        if (slide !== undefined && gallery?.go) {
          this._skipGalleryOnce = true;
          gallery.go(Number(slide), true);
        }
        this.updateVariant();
      }, { signal: this.abort.signal });
      this.addEventListener('submit', () => {
        pendingCartScroll = { y: window.scrollY, savedAt: Date.now() };
        window.__b5CartScrollY = pendingCartScroll.y;
      }, { signal: this.abort.signal });
      this.updateVariant();
    }
    syncGalleryToIndex(index) {
      if (this._syncingGallery) return;
      const radios = [...this.querySelectorAll('[data-b5-option] input[type="radio"][data-b5-gallery-slide]')];
      const match = radios.find(radio => Number(radio.dataset.b5GallerySlide) === index);
      if (!match || match.checked) return;
      this._syncingGallery = true;
      match.checked = true;
      this._skipGalleryOnce = true;
      this.updateVariant();
      this._syncingGallery = false;
    }
    updateVariant() {
      const options = [...this.querySelectorAll('[data-b5-option]')].sort((a, b) => Number(a.dataset.b5Option) - Number(b.dataset.b5Option)).map(el => el.tagName === 'SELECT' ? el.value : el.querySelector('input:checked')?.value);
      const variant = this.variants.find(v => v.options.length === options.length && v.options.every((value, i) => value === options[i]));
      const input = this.querySelector('[name="id"]');
      const button = this.querySelector('[name="add"]');
      const isAvailable = Boolean(variant?.available);
      if (input) { input.value = variant?.id || ''; input.disabled = !variant; }
      if (button) {
        button.disabled = !isAvailable;
        // Preserve the product-form's pending state while a cart request is in flight.
        if (!button.classList.contains('is-loading')) button.removeAttribute('aria-disabled');
        const label = this.querySelector('[data-b5-add-text]');
        if (label) label.textContent = !variant ? this.dataset.unavailableLabel : isAvailable ? this.dataset.addLabel : this.dataset.soldLabel;
      }
      const status = this.querySelector('[data-b5-status]');
      if (status) status.textContent = !variant ? this.dataset.unavailableLabel : !isAvailable ? this.dataset.soldLabel : '';
      const price = this.querySelector('[data-b5-price]');
      if (price && !price.hasAttribute('data-custom-price')) {
        price.querySelector('[data-b5-current]').textContent = String(variant?.price || '').replace(/,/g, '');
        const compare = price.querySelector('[data-b5-compare]');
        const configuredCompare = price.dataset.b5ConfiguredCompare || '';
        const fallbackCompare = campaignComparePrice(variant?.price || '');
        compare.textContent = String(configuredCompare || variant?.compare || fallbackCompare).replace(/,/g, '');
        compare.dataset.b5Fallback = compare.textContent;
        compare.hidden = !(configuredCompare || variant?.onSale || fallbackCompare);
      }
      if (!variant) return;
      const cardImages = [...this.querySelectorAll('[data-b5-card-image]')];
      if (cardImages.length) {
        let map = {};
        try { map = JSON.parse(this.querySelector('[data-b5-card-image-map]')?.textContent || '{}'); } catch { map = {}; }
        const matchMap = value => {
          const key = String(value || '').toLowerCase().trim();
          if (!key) return -1;
          if (Object.prototype.hasOwnProperty.call(map, key)) return Number(map[key]);
          const alias = Object.keys(map).find(name => key.includes(name) || name.includes(key));
          return alias ? Number(map[alias]) : -1;
        };
        let imageIndex = -1;
        variant.options.forEach(value => { if (imageIndex < 0) imageIndex = matchMap(value); });
        const target = cardImages.find(image => Number(image.dataset.b5CardImage) === imageIndex) || cardImages[0];
        cardImages.forEach(image => { image.hidden = image !== target; });
      }
      const gallery = this.querySelector('b5-gallery');
      if (gallery?.slides?.length) {
        let index = -1;
        if (gallery.dataset.b5CustomGallery === 'true') {
          let map = {};
          try { map = JSON.parse(this.querySelector('[data-b5-gallery-map]')?.textContent || '{}'); } catch { map = {}; }
          const matchMap = (value) => {
            const key = String(value || '').toLowerCase().trim();
            if (!key) return -1;
            if (Object.prototype.hasOwnProperty.call(map, key)) return Number(map[key]);
            const alias = Object.keys(map).find(name => key.includes(name) || name.includes(key));
            return alias ? Number(map[alias]) : -1;
          };
          const colorField = this.querySelector('[data-b5-option] input[type="radio"]:checked, [data-b5-option] select');
          index = matchMap(colorField?.value);
          if (index < 0) variant.options.forEach(value => { if (index < 0) index = matchMap(value); });
          const mappedSlide = colorField?.dataset?.b5GallerySlide;
          if (index < 0 && mappedSlide !== undefined) index = Number(mappedSlide);
        } else {
          index = gallery.slides.findIndex(slide => Number(slide.dataset.mediaId) === Number(variant.mediaId));
        }
        if (index >= 0 && !this._skipGalleryOnce) gallery.go(index, true);
        this._skipGalleryOnce = false;
      }
      const cardImage = this.querySelector('.b5-card__image');
      if (cardImage && variant.image && !this.hasAttribute('data-b5-fixed-image')) { cardImage.src = variant.image; cardImage.removeAttribute('srcset'); }
      this.querySelectorAll('[data-b5-product-link]').forEach(link => {
        const url = new URL(this.dataset.productUrl || link.href, window.location.origin);
        url.searchParams.set('variant', variant.id);
        link.href = url.toString();
      });
    }
    disconnectedCallback() { this.abort?.abort(); this.abort = null; }
  }
  if (!customElements.get('b5-gallery')) customElements.define('b5-gallery', BlackFiveGallery);
  if (!customElements.get('b5-product')) customElements.define('b5-product', BlackFiveProduct);
  const rememberCartScroll = event => {
    if (!event.target.closest?.('b5-product button[name="add"]')) return;
    pendingCartScroll = { y: window.scrollY, savedAt: Date.now() };
    window.__b5CartScrollY = pendingCartScroll.y;
  };
  document.addEventListener('pointerdown', rememberCartScroll, true);
  document.addEventListener('click', rememberCartScroll, true);
  document.addEventListener('on:cart-drawer:after-close', () => {
    pendingCartScroll = null;
    delete window.__b5CartScrollY;
  });
  // Delegation survives theme-editor section replacement. Native dialog handles Escape and focus trapping.
  document.addEventListener('click', event => {
    const open = event.target.closest('[data-b5-dialog-open]');
    if (open) { const dialog = document.getElementById(open.dataset.b5DialogOpen); if (dialog && !dialog.open) dialog.showModal(); }
    const close = event.target.closest('[data-b5-dialog-close]');
    if (close) close.closest('dialog')?.close();
    if (event.target.matches('dialog.b5-dialog')) {
      const rect = event.target.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) event.target.close();
    }
  });
  document.addEventListener('shopify:block:select', event => {
    const gallery = event.target.closest('b5-gallery');
    const slide = event.target.closest('[data-b5-slide]');
    if (gallery?.slides && slide) { gallery.stop(); gallery.go(gallery.slides.indexOf(slide), true); }
  });
  const countdowns = new Map();
  function initCountdowns(root = document) {
    root.querySelectorAll('[data-b5-countdown]').forEach(el => {
      if (countdowns.has(el)) return;
      const end = Date.parse(el.dataset.b5Countdown);
      if (!Number.isFinite(end)) { el.hidden = true; return; }
      const tick = () => {
        if (!el.isConnected) { clearInterval(countdowns.get(el)); countdowns.delete(el); return; }
        const seconds = Math.max(0, Math.floor((end - Date.now()) / 1000));
        const display = el.querySelector('[data-b5-clock]');
        if (!seconds) { el.textContent = el.dataset.ended; clearInterval(countdowns.get(el)); return; }
        display.textContent = `${Math.floor(seconds / 86400)}d ${String(Math.floor(seconds / 3600) % 24).padStart(2, '0')}h ${String(Math.floor(seconds / 60) % 60).padStart(2, '0')}m ${String(seconds % 60).padStart(2, '0')}s`;
      };
      countdowns.set(el, setInterval(tick, 1000)); tick();
    });
  }
  initCountdowns();
  document.addEventListener('shopify:section:load', event => initCountdowns(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-b5-countdown]').forEach(el => { clearInterval(countdowns.get(el)); countdowns.delete(el); });
  });
})();
